import type { ReactNode } from "react";
import type {
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { createStore, useStore } from "zustand";

interface ChatStore {
  isChatLoading: boolean;
  messages: ChatMessage[];
  viewingVersionId: string | null;
  actions: {
    setIsChatLoading: (isChatLoading: boolean) => void;
    setMessages: (messages: ChatMessage[]) => void; // For server sync
    clearMessages: () => void;
    addMessages: (messages: ChatMessage[]) => void;
    addOrUpdateMessage: (
      messageId: string,
      content: ChatMessageContent,
      role: "assistant" | "tool",
      chatId: string,
    ) => void;
    setViewingVersionId: (viewingVersionId: string | null) => void;
    resetChatStore: () => void;
  };
}

const createChatStore = () => {
  return createStore<ChatStore>((set) => ({
    isChatLoading: false,
    messages: [],
    viewingVersionId: null,
    actions: {
      setIsChatLoading: (isChatLoading: boolean) => set({ isChatLoading }),
      setMessages: (messages: ChatMessage[]) => set({ messages }),
      clearMessages: () => set({ messages: [] }),
      addMessages: (newMessages: ChatMessage[]) =>
        set((state) => ({ messages: [...state.messages, ...newMessages] })),
      addOrUpdateMessage: (messageId, content, role, chatId) =>
        set((state) => {
          const existingMessage = state.messages.find(
            (msg) => msg.id === messageId,
          );

          if (existingMessage) {
            return {
              messages: state.messages.map((msg) =>
                msg.id === messageId
                  ? { ...msg, content, createdAt: new Date() }
                  : msg,
              ),
            };
          } else {
            return {
              messages: [
                ...state.messages,
                {
                  id: messageId,
                  content,
                  createdAt: new Date(),
                  role,
                  chatId,
                },
              ],
            };
          }
        }),
      setViewingVersionId: (viewingVersionId: string | null) =>
        set({ viewingVersionId }),
      resetChatStore: () =>
        set({
          isChatLoading: false,
          messages: [],
          viewingVersionId: null,
        }),
    },
  }));
};

type ChatContextType = {
  store: ReturnType<typeof createChatStore>;
};

const ChatContext = createContext<ChatContextType | null>(null);

const useChatContext = () => {
  return useRequiredContext(ChatContext);
};

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [store] = React.useState(() => createChatStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

// Main hook to access the store
function useChatStore<T = ChatStore>(selector?: (state: ChatStore) => T): T {
  const { store } = useChatContext();
  return useStore(store, selector as any);
}

// Convenience hooks for accessing specific values from the store
export const useIsChatLoading = () =>
  useChatStore((state) => state.isChatLoading);

export const useMessages = () => useChatStore((state) => state.messages);

export const useViewingVersionId = () =>
  useChatStore((state) => state.viewingVersionId);

export const useChatStoreActions = () => useChatStore((state) => state.actions);
